import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  PlusCircleIcon,
  ArrowUpTrayIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { cn } from "@/utils";
import { Lab, LAB } from "@/components/ms";

const ACTIONS = [
  {
    title: "Add source",
    subtitle: "Connect an exchange or wallet",
    icon: PlusCircleIcon,
    href: "/settings/exchange/new",
  },
  {
    title: "Import CSV",
    subtitle: "Upload transaction files",
    icon: ArrowUpTrayIcon,
    href: "/import",
  },
  {
    title: "Tax reports",
    subtitle: "View tax calculations",
    icon: ChartBarIcon,
    href: "/tax/cgt",
  },
  {
    title: "Settings",
    subtitle: "Account preferences",
    icon: Cog6ToothIcon,
    href: "/settings/profile",
  },
];

export const QuickActionsWidget = ({ compact = false }: { compact?: boolean }) => {
  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem("quickActionsCollapsed") !== "false"
  );

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem("quickActionsCollapsed", String(newState));
  };

  // Compact mode: icon buttons beside the screen heading
  if (compact) {
    return (
      <div className="flex gap-2">
        {ACTIONS.map((action) => (
          <Link
            key={action.title}
            to={action.href}
            className="grid h-11 w-11 place-items-center border border-ms-line text-ms-mute transition-colors hover:border-ms-line2 hover:text-ms-ink"
            aria-label={`${action.title} — ${action.subtitle}`}
            title={action.title}
          >
            <action.icon className="h-[18px] w-[18px]" aria-hidden="true" />
          </Link>
        ))}
      </div>
    );
  }

  // Full mode
  return (
    <div className="flex flex-col">
      <div className="flex items-baseline justify-between gap-3 border-b border-ms-line pb-[7px]">
        <Lab>Quick actions</Lab>
        <button
          type="button"
          onClick={toggleCollapse}
          className="grid h-8 w-8 place-items-center text-ms-dim transition-colors hover:text-ms-ink"
          aria-label={isCollapsed ? "Expand quick actions" : "Collapse quick actions"}
        >
          {isCollapsed ? (
            <ChevronDownIcon className="h-4 w-4" />
          ) : (
            <ChevronUpIcon className="h-4 w-4" />
          )}
        </button>
      </div>

      {isCollapsed ? (
        <div className="mt-3 grid grid-cols-4 gap-2">
          {ACTIONS.map((action) => (
            <Link
              key={action.title}
              to={action.href}
              className={cn(
                LAB,
                "flex min-h-[64px] flex-col items-center justify-center gap-2 border border-ms-line px-2 py-3 text-center text-[9px] text-ms-mute transition-colors hover:border-ms-line2 hover:text-ms-ink"
              )}
            >
              <action.icon className="h-[18px] w-[18px]" aria-hidden="true" />
              {action.title}
            </Link>
          ))}
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {ACTIONS.map((action) => (
            <Link
              key={action.title}
              to={action.href}
              className="flex min-h-[56px] items-center gap-3 border border-ms-line px-3 py-2.5 transition-colors hover:border-ms-line2 hover:bg-ms-card2"
            >
              <action.icon className="h-5 w-5 flex-none text-ms-mute" aria-hidden="true" />
              <span className="min-w-0">
                <span className="block text-[13.5px] font-medium tracking-[-0.01em]">
                  {action.title}
                </span>
                <Lab size={9} className="mt-[3px] block">
                  {action.subtitle}
                </Lab>
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
